/*
 * Paint-by-numbers photo checker.
 *
 * Answers the question people actually ask before buying a custom kit: "will
 * my photo work?" Three heuristics, all measured in-browser on a canvas — the
 * photo is never uploaded, which is both the honest privacy claim and the
 * reason this needs no backend.
 *
 * These are heuristics, not guarantees, and the copy says so. The point is to
 * catch the photos that reliably produce a bad kit (too small, too blurry, too
 * flat) before someone pays for one, and to move everyone else towards an
 * order with a size that suits their resolution.
 */
(function () {
  'use strict';

  // A kit is printed at roughly 100dpi on canvas. Below ~1.5x the pixel count
  // the print needs, numbered regions start landing on top of image noise.
  var SIZES = [
    { id: '30x40', label: '30×40cm', minPixels: 1.0e6 },
    { id: '40x50', label: '40×50cm', minPixels: 2.0e6 },
    { id: '50x65', label: '50×65cm', minPixels: 3.5e6 }
  ];

  var MAX_ANALYSIS_EDGE = 480; // enough signal, and keeps this instant on phones

  function greyscale(data, w, h) {
    var g = new Float32Array(w * h);
    for (var i = 0, p = 0; i < g.length; i++, p += 4) {
      g[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
    }
    return g;
  }

  // Variance of the Laplacian — the standard cheap focus measure. High variance
  // means lots of sharp intensity transitions, i.e. an in-focus image.
  function sharpness(g, w, h) {
    var vals = [], sum = 0;
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        var i = y * w + x;
        var lap = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w];
        vals.push(lap);
        sum += lap;
      }
    }
    if (!vals.length) return 0;
    var mean = sum / vals.length, acc = 0;
    for (var k = 0; k < vals.length; k++) { var d = vals[k] - mean; acc += d * d; }
    return acc / vals.length;
  }

  // Spread of luminance. A flat, low-contrast photo collapses into a handful of
  // near-identical paints and reads as mud on canvas.
  function contrast(g) {
    var sum = 0, i;
    for (i = 0; i < g.length; i++) sum += g[i];
    var mean = sum / g.length, acc = 0;
    for (i = 0; i < g.length; i++) { var d = g[i] - mean; acc += d * d; }
    return Math.sqrt(acc / g.length);
  }

  function band(value, ok, good) {
    if (value >= good) return 2;
    if (value >= ok) return 1;
    return 0;
  }

  function recommendedSize(pixels) {
    var best = SIZES[0];
    for (var i = 0; i < SIZES.length; i++) if (pixels >= SIZES[i].minPixels) best = SIZES[i];
    return best;
  }

  function analyse(img) {
    var pixels = img.naturalWidth * img.naturalHeight;
    var scale = Math.min(1, MAX_ANALYSIS_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    var w = Math.max(1, Math.round(img.naturalWidth * scale));
    var h = Math.max(1, Math.round(img.naturalHeight * scale));

    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var g = greyscale(ctx.getImageData(0, 0, w, h).data, w, h);
    var sharp = sharpness(g, w, h);
    var cont = contrast(g);

    var checks = [
      {
        key: 'resolution',
        label: 'Resolution',
        score: band(pixels, 1.0e6, 2.0e6),
        detail: img.naturalWidth + ' × ' + img.naturalHeight + ' (' + (pixels / 1e6).toFixed(1) + ' megapixels)',
        advice: {
          0: 'Too small for a printed canvas — numbered regions will land on image noise. Use the original file rather than one saved from a messaging app.',
          1: 'Fine for a 30×40cm canvas. A larger one would show the limits of this file.',
          2: 'Plenty of detail for any canvas size we make.'
        }
      },
      {
        key: 'sharpness',
        label: 'Sharpness',
        score: band(sharp, 60, 200),
        detail: 'focus score ' + Math.round(sharp),
        advice: {
          0: 'Looks soft or motion-blurred. Blur becomes vague, wandering region edges that are hard to paint.',
          1: 'Acceptable, though a sharper shot would give cleaner edges around the subject.',
          2: 'Crisp edges — regions will come out well defined.'
        }
      },
      {
        key: 'contrast',
        label: 'Contrast',
        score: band(cont, 35, 55),
        detail: 'tonal spread ' + Math.round(cont),
        advice: {
          0: 'Very flat. Similar tones collapse into near-identical paints, so the finished piece reads as one colour.',
          1: 'Workable. Lifting contrast a little before uploading would separate the colours further.',
          2: 'Strong light and shade — this separates cleanly into distinct paints.'
        }
      }
    ];

    var total = checks.reduce(function (s, x) { return s + x.score; }, 0);
    var verdict = total >= 5
      ? { id: 'great', title: 'This photo will make a great kit', line: 'All three checks look strong.' }
      : total >= 3
        ? { id: 'ok', title: 'This photo will work', line: 'Good enough for a kit, with one or two things worth improving first.' }
        : { id: 'risky', title: 'This photo will struggle', line: 'We can still make a kit from it, but the result is unlikely to please you.' };

    return { checks: checks, verdict: verdict, size: recommendedSize(pixels), pixels: pixels };
  }

  function mount(root) {
    var input = root.querySelector('[data-pc-input]');
    var dropzone = root.querySelector('[data-pc-dropzone]');
    var results = root.querySelector('[data-pc-results]');
    var preview = root.querySelector('[data-pc-preview]');
    var verdictEl = root.querySelector('[data-pc-verdict]');
    var checksEl = root.querySelector('[data-pc-checks]');
    var sizeEl = root.querySelector('[data-pc-size]');
    var cta = root.querySelector('[data-pc-cta]');
    var errEl = root.querySelector('[data-pc-error]');
    if (!input || !results) return;

    var productPath = root.dataset.product || '/products/custom-paint-by-numbers';
    var rootUrl = root.dataset.root || '/';
    var discount = root.dataset.discount || '';
    var prefix = rootUrl === '/' ? '' : rootUrl;

    function track(name, params) {
      if (window.gtag) window.gtag('event', name, Object.assign({ event_category: 'photo_checker' }, params || {}));
    }

    function fail(message) {
      errEl.textContent = message;
      errEl.hidden = false;
      results.hidden = true;
    }

    function render(report) {
      errEl.hidden = true;
      verdictEl.className = 'pc-verdict is-' + report.verdict.id;
      verdictEl.querySelector('[data-pc-verdict-title]').textContent = report.verdict.title;
      verdictEl.querySelector('[data-pc-verdict-line]').textContent = report.verdict.line;

      checksEl.innerHTML = '';
      report.checks.forEach(function (c) {
        var li = document.createElement('li');
        li.className = 'pc-check is-' + (c.score === 2 ? 'good' : c.score === 1 ? 'fair' : 'poor');
        li.innerHTML =
          '<span class="pc-check__label"></span>' +
          '<span class="pc-check__detail"></span>' +
          '<span class="pc-check__advice"></span>';
        li.querySelector('.pc-check__label').textContent = c.label;
        li.querySelector('.pc-check__detail').textContent = c.detail;
        li.querySelector('.pc-check__advice').textContent = c.advice[c.score];
        checksEl.appendChild(li);
      });

      sizeEl.textContent = report.size.label;

      var params = new URLSearchParams({
        utm_source: 'photo_checker', utm_medium: 'tool', utm_campaign: 'pbn_photo_checker',
        size: report.size.id
      });
      var dest = prefix + productPath + '?' + params.toString();
      cta.href = discount
        ? prefix + '/discount/' + encodeURIComponent(discount) + '?redirect=' + encodeURIComponent(productPath + '?' + params.toString())
        : dest;

      results.hidden = false;
      track('photo_checked', { verdict: report.verdict.id, recommended_size: report.size.id });
      results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function handle(file) {
      if (!file) return;
      if (!/^image\//.test(file.type)) return fail('That file is not an image. Upload a JPG, PNG or HEIC photo.');
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        preview.src = url;
        try {
          render(analyse(img));
        } catch (e) {
          fail('Could not read that image. Try a different file.');
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        fail('Could not open that image. Try a JPG or PNG.');
      };
      img.src = url;
    }

    input.addEventListener('change', function () { handle(this.files && this.files[0]); });
    if (cta) cta.addEventListener('click', function () { track('photo_checker_cta_click'); });

    if (dropzone) {
      ['dragenter', 'dragover'].forEach(function (t) {
        dropzone.addEventListener(t, function (e) { e.preventDefault(); dropzone.classList.add('is-over'); });
      });
      ['dragleave', 'drop'].forEach(function (t) {
        dropzone.addEventListener(t, function (e) { e.preventDefault(); dropzone.classList.remove('is-over'); });
      });
      dropzone.addEventListener('drop', function (e) {
        handle(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-pbn-photo-checker]').forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Exposed for tests/photo-checker.check.mjs — the scoring is the part worth
  // pinning down, and it is pure.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { analyse: analyse, recommendedSize: recommendedSize, band: band, SIZES: SIZES };
  }
})();
